import { Modal, ModalContent, ModalHeader, ModalBody, Textarea, ScrollShadow } from "@heroui/react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { blurActiveElement } from "@/utils/dom";
import { logger } from "@/telemetry";

interface PromptSuggestion {
  id: string;
  title: string;
  description: string;
  content: string;
}

interface PromptSearchModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTemplate: (template: string) => void;
}

export function PromptSearchModal({
  isOpen,
  onOpenChange,
  onSelectTemplate,
}: PromptSearchModalProps) {
  const [templateInput, setTemplateInput] = useState("");
  const [suggestions, setSuggestions] = useState<PromptSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { adminClient } = useWebuildAPIClient();
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();

  const searchTemplates = async (searchText: string) => {
    setIsLoading(true);

    logger.info("searchTemplates", { searchText });
    try {
      setSuggestions([]);
      const response = await adminClient?.prompts.search({ searchText });

      if (!response) {
        logger.error("No response from adminClient?.prompts.generate");
        setSuggestions([]);

        return;
      }
      setSuggestions(
        response.map((prompt) => ({
          id: prompt.id,
          title: prompt.name,
          description: prompt.description,
          content: prompt.content,
        })),
      );
    } catch (error) {
      logger.error("Error searching templates", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInitialPrompts = useCallback(async () => {
    setIsLoading(true);
    try {
      setSuggestions([]);
      const response = await adminClient?.prompts.listPrompts({});

      if (!response) {
        setSuggestions([]);

        return;
      }
      setSuggestions(
        response.map((prompt) => ({
          id: prompt.id,
          title: prompt.name,
          description: prompt.description,
          content: prompt.content,
        })),
      );
    } catch (error) {
      logger.error("Error fetching initial prompts", error);
    } finally {
      setIsLoading(false);
    }
  }, [adminClient]);

  // Handle modal close with focus cleanup
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      blurActiveElement();
    }
    onOpenChange(open);
  };

  const handleTemplateSelect = (template: string) => {
    onSelectTemplate(template);
    handleOpenChange(false);
  };

  useEffect(() => {
    if (isOpen) {
      setTemplateInput("");
      fetchInitialPrompts();
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [isOpen, fetchInitialPrompts]);

  return (
    <Modal
      classNames={{
        closeButton: "top-3 right-3 p-0 hover:bg-default-100",
      }}
      data-id="prompt-search-modal"
      isOpen={isOpen}
      size="4xl"
      onOpenChange={handleOpenChange}
    >
      <ModalContent>
        <>
          <ModalHeader className="flex flex-col gap-1" data-id="prompt-search-modal-header">
            {t("Prompts")}
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-col gap-4">
              <Textarea
                data-id="prompt-search-input"
                minRows={3}
                placeholder="Search for a prompt..."
                value={templateInput}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    searchTemplates(templateInput);
                  }
                }}
                onValueChange={(value) => {
                  setTemplateInput(value);
                  if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
                  }
                  debounceTimeoutRef.current = setTimeout(() => {
                    searchTemplates(value);
                  }, 3000);
                }}
              />
              <div className="flex flex-col gap-2">
                <h3 className="text-sm font-medium" data-id="prompt-search-status">
                  {isLoading
                    ? "Searching for prompts..."
                    : suggestions.length > 0
                      ? "Suggested Prompts:"
                      : ""}
                </h3>
                <ScrollShadow
                  className="w-full max-h-[50vh] overflow-y-auto flex flex-col gap-2"
                  data-id="prompt-suggestions-container"
                >
                  {suggestions.map((suggestion) => (
                    <div
                      key={suggestion.id}
                      className="flex flex-col items-start gap-2 bg-default-100 p-4 rounded-medium cursor-pointer hover:bg-default-200 max-h-32 overflow-hidden shrink-0"
                      data-id={`prompt-suggestion-${suggestion.id}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleTemplateSelect(suggestion.content)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleTemplateSelect(suggestion.content);
                        }
                      }}
                    >
                      <span
                        className="text-sm font-bold"
                        data-id={`prompt-suggestion-title-${suggestion.id}`}
                      >
                        {suggestion.title}
                      </span>
                      <span
                        className="text-xs text-default-500 whitespace-pre-wrap break-words"
                        data-id={`prompt-suggestion-description-${suggestion.id}`}
                      >
                        {suggestion.description?.length > 300
                          ? suggestion.description.slice(0, 300) + "..."
                          : suggestion.description}
                      </span>
                      <span
                        className="text-xs text-default-500 whitespace-pre-wrap break-words"
                        data-id={`prompt-suggestion-content-${suggestion.id}`}
                      >
                        {suggestion.content?.length > 300
                          ? suggestion.content.slice(0, 300) + "..."
                          : suggestion.content}
                      </span>
                    </div>
                  ))}
                </ScrollShadow>
              </div>
            </div>
          </ModalBody>
        </>
      </ModalContent>
    </Modal>
  );
}
